import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {JwtHelperService} from '@auth0/angular-jwt';
import {getAuth} from '~feature/feature-auth/feature-auth.selectors';
import {asyncScheduler, BehaviorSubject, interval, Observable, Subscription} from 'rxjs';
import {authSet} from '~feature/feature-auth/feature-auth.actions';
import {AuthNgrx, emptyAuth, getRoles} from '~model/ngrx/auth-ngrx';
import {observeOn} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {ReduxService} from '~core/utils/redux.service';
import {LSItem} from '~utils/storage-utils';
import {ParamService} from '~core/utils/param.service';
import { AuthVo } from '~model/vo/auth-vo';
import { environment } from '~env/environment';
import {ApibaseService} from '~core/api/apibase.service';
import {DEFAULT_TIMEOUT} from '~utils/request-metadata';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ApibaseService {

  prefixoUrl = 'controlTower';
  urlFunction = 'authenticate';

  authNgrx$ = new BehaviorSubject<AuthNgrx>(emptyAuth());
  private realtime = interval(1000);
  private subs: Subscription[] = [];
  private readonly urlLogin = '/login';

  private behaviorTokenAuth$ = new BehaviorSubject<LoginResult>('NONE');
  readonly tokenAuth$ = this.behaviorTokenAuth$ as Observable<LoginResult>;

  constructor(
    protected http: HttpClient,
    private router: Router,
    private store: Store<AuthNgrx>,
    private reduxService: ReduxService,
    public jwt: JwtHelperService,
    private dialogRef: MatDialog,
    private params: ParamService
  ) {
    super(http);
  }

  nextTokenAuth(tokenValido: LoginResult): void {
    this.behaviorTokenAuth$.next(tokenValido);
  }

  inicializar(): void {
    const auth = authByStorage();
    this.store.dispatch(authSet(auth));

    this.store
      .pipe(select(getAuth))
      .pipe(observeOn(asyncScheduler))
      .subscribe(res => {
        this.authNgrx$.next(res);
        const authParam = authByStorage();
        const logado = !this.jwt.isTokenExpired(authParam.token || '');
        logado ? this.ativar(authParam) : this.desativar();
      });
  }

  ativar(res: AuthNgrx): void {
    const tokenDecoded = this.jwt.decodeToken(res.token || '');
    if (!tokenDecoded) {
      this.behaviorTokenAuth$.next('INVALID');
    }
    const [nickname, empresa] = tokenDecoded?.sub?.split('@');
    localStorage.setItem(LSItem.nickname, nickname);
    localStorage.setItem(LSItem.token, res.token ?? '');
    localStorage.setItem(LSItem.tokenRules, res.tokenRules ?? '');
    localStorage.setItem(LSItem.email, res.email ?? '');
    localStorage.setItem(LSItem.empresa, empresa);
    localStorage.setItem(LSItem.linkLogo, res.linkLogo ?? '');
    localStorage.setItem(LSItem.nomeEmpresa, res.nomeEmpresa ?? '');

    const url = this.router.routerState.snapshot.url;
    if (url.startsWith('/login')) {
      const params = this.router.routerState.snapshot.root.queryParams;
      let urlTo = '/';
      if (params && params.return) {
        urlTo += params.return;
      }
      this.router.navigateByUrl(urlTo).then();
    }
    this.ativarGuardas();
  }

  desativar(logoutTokenSession = false): void {
    this.behaviorTokenAuth$.next('NONE');
    this.params.reset();
    this.dialogRef.closeAll();
    this.desativarGuardas();
    this.reduxService.reset();
    let extras;
    const url = this.router.routerState.snapshot.url;

    localStorage.clear();

    if (environment.tokenSession) {
      const logoutRedirectUrl = environment.logoutRedirectSession && sessionStorage.getItem(environment.logoutRedirectSession);
      if (logoutTokenSession && logoutRedirectUrl) {
        let aux = '';
        if (logoutRedirectUrl.includes('/pathfind') && !logoutRedirectUrl.includes('principal.xhtml')) {
          aux = (!logoutRedirectUrl.endsWith('/') ? '/' : '') + 'pages/pathfind/principal.xhtml';
        }
        sessionStorage.clear();
        setTimeout(() => {
          location.href = logoutRedirectUrl + aux;
        }, 1500);
      }
    }
    if (url && !url.startsWith(this.urlLogin)) {
      extras = {queryParams: {return: url.replace('/', '')}};
      this.router.navigate([this.urlLogin], extras).then();
      window.location.reload();
    }
  }

  conectar(objEnvio: AuthVo): Observable<AuthNgrx> {
    const scope = !environment.experimentalFeatures?.showChat ? 'without_chat' : undefined;
    this.behaviorTokenAuth$.next(objEnvio.senha ? 'LOADING' : 'NONE');
    return super.post<AuthNgrx>({timeout: DEFAULT_TIMEOUT.MIDDLE, objEnvio: {...objEnvio, scope}})
      .pipe(this.defaultCatch(emptyAuth()));
    this.params.reset();
  }

  conectarToken(objEnvio: { token: string }): Observable<AuthNgrx> {
    const scope = !environment.experimentalFeatures?.showChat ? 'without_chat' : undefined;
    this.behaviorTokenAuth$.next(objEnvio.token ? 'LOADING' : 'LOGOUT');
    if (!objEnvio.token) {
      return new BehaviorSubject<AuthNgrx>(emptyAuth(true));
    }
    return super.post<AuthNgrx>({extraUrl: 'token' , timeout: DEFAULT_TIMEOUT.MIDDLE, objEnvio: {...objEnvio, scope}})
      .pipe(this.defaultCatch(emptyAuth()));
  }

  private ativarGuardas(): void {
    this.subs.push(this.realtime.subscribe(() => !localStorage.getItem(LSItem.token) && this.desativar()));
  }

  private desativarGuardas(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}

const authByStorage = (): AuthNgrx => ({
  token: localStorage.getItem(LSItem.token),
  tokenRules: localStorage.getItem(LSItem.tokenRules),
  nickname: localStorage.getItem(LSItem.nickname),
  email: localStorage.getItem(LSItem.email),
  empresa: localStorage.getItem(LSItem.empresa),
  nomeEmpresa: localStorage.getItem(LSItem.nomeEmpresa),
  linkLogo: localStorage.getItem(LSItem.linkLogo),
  roles: getRoles(localStorage.getItem(LSItem.token) ?? ''),
});

export type LoginResult = 'LOGOUT' | 'LOADING' | 'SUCCESS' | 'EXPIRED' | 'INVALID' | 'NONE';
